import { SWAP_DEFAULTS } from '../lib/config'

/**
 * The only two values in this app the user sets themselves, and the only two the `/swap`
 * endpoint accepts from a client beyond the recipient. Everything else about the
 * transaction — the legs, the percentages, the amounts — comes from the plan the server
 * quoted, which is what makes a route id worth anything.
 *
 * The bounds mirror the API's `swap` config. They are enforced there regardless; repeating
 * them here just means a rejected value is caught before a round trip.
 */
export function SettingsPanel({
  slippageBps,
  deadlineMinutes,
  onSlippage,
  onDeadline,
  onClose,
}: {
  slippageBps: number
  deadlineMinutes: number
  onSlippage: (bps: number) => void
  onDeadline: (minutes: number) => void
  onClose: () => void
}) {
  const maxMinutes = Math.floor(SWAP_DEFAULTS.maxDeadlineSeconds / 60)

  return (
    <div className="settings">
      <div className="settings-row">
        <label htmlFor="slippage">Slippage tolerance</label>
        <div className="settings-controls">
          {[10, 50, 100].map((preset) => (
            <button
              key={preset}
              type="button"
              className={`chip ${slippageBps === preset ? 'chip-active' : ''}`}
              onClick={() => onSlippage(preset)}
            >
              {preset / 100}%
            </button>
          ))}
          <input
            id="slippage"
            className="input input-small"
            type="number"
            min={SWAP_DEFAULTS.minSlippageBps}
            max={SWAP_DEFAULTS.maxSlippageBps}
            value={slippageBps}
            onChange={(event) => {
              const value = Number(event.target.value)
              if (Number.isInteger(value)) {
                onSlippage(Math.min(SWAP_DEFAULTS.maxSlippageBps, Math.max(SWAP_DEFAULTS.minSlippageBps, value)))
              }
            }}
          />
          <span className="muted">bps</span>
        </div>
      </div>

      <p className="hint">
        Checked once, in aggregate. Every leg of a split pays the router with no minimum of its own and a single
        trailing sweep enforces this tolerance on the total — so a split is not three chances to slip.
      </p>

      <div className="settings-row">
        <label htmlFor="deadline">Deadline</label>
        <div className="settings-controls">
          <input
            id="deadline"
            className="input input-small"
            type="number"
            min={1}
            max={maxMinutes}
            value={deadlineMinutes}
            onChange={(event) => {
              const value = Number(event.target.value)
              if (Number.isInteger(value)) onDeadline(Math.min(maxMinutes, Math.max(1, value)))
            }}
          />
          <span className="muted">minutes (max {maxMinutes})</span>
        </div>
      </div>

      <button type="button" className="btn btn-ghost btn-small" onClick={onClose}>
        Done
      </button>
    </div>
  )
}
